import React, { useState, ReactNode } from "react";
import { VueContextType } from "@iloq/shared-react/vueContext";
import {
  Box,
  Button,
  Link,
  Text,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Image,
} from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { LocaleChanger } from "./locale-changer";

type LoginProps = { setLocale: (locale: string) => void } & VueContextType;

export function Login(props: LoginProps): ReactNode {
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const { isOpen, onClose } = useDisclosure({
    isOpen: showForgotPasswordDialog,
    onClose: () => setShowForgotPasswordDialog(false),
  });

  return (
    <main>
      <Flex
        position={"fixed"}
        inset="0"
        px={{ base: "md", md: "4xl" }}
        py={{ base: "5vh", md: "10vh" }}
        gap="4xl"
        overflow="auto"
      >
        <Box
          display={{ base: "none", md: "block" }}
          backgroundImage="url('/img/login/iloq-login.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
          flex="1 0 auto"
          borderRadius="rounder"
        />
        <Box w={{ base: "100%", md: "35%" }}>
          <Flex
            flexDirection="column"
            textAlign="left"
            pt={{ base: "base", sm: "15vh", md: "2xl", xl: "10vh" }}
            m="0 5% 0 5%"
            data-cy="login-form"
          >
            <Box textAlign="right">
              <LocaleChanger setLocale={props.setLocale} />
            </Box>
            <Flex pt={{ base: "5vh", lg: "10vh" }} flexDirection="column">
              <Flex alignContent="center">
                <img src="/img/iloq-partner-portal-login-logo.svg" alt="Partner Portal Logo" />
              </Flex>
              <Flex
                fontSize="12pt"
                lineHeight="1.5rem"
                color="black"
                pt="xl"
                pb="xl"
                data-cy="login-text"
                flexDirection="column"
                alignItems="flex-start"
              >
                <Text mb="xl">
                  <FormattedMessage id="login.instructionText" />
                </Text>
                <Button as="a" href="/user/login" variant="beigePill" data-cy="login-button">
                  <FormattedMessage id="login.button" />
                </Button>
                <Link
                  as="button"
                  onClick={() => setShowForgotPasswordDialog(true)}
                  textDecoration="none"
                  fontWeight="700"
                  mt="20px"
                >
                  <FormattedMessage id="login.forgotPassword.linkText" />
                </Link>
              </Flex>
              <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent p="md" borderRadius="base">
                  <ModalHeader>
                    <Heading size="md" fontWeight={500}>
                      <FormattedMessage id="login.forgotPassword.dialog.header" />
                    </Heading>
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text pb="md" fontSize="md">
                      <FormattedMessage id="login.forgotPassword.dialog.body" />
                    </Text>
                    <Flex my="xl" wrap="wrap">
                      <Box flex="1" textAlign="center" minW="50%">
                        <Link
                          href="https://account.live.com/password/reset"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Flex height="40px" alignItems="center" justifyContent="center">
                            <Image
                              src="/img/login/microsoft_logo.svg"
                              alt="Microsoft Logo"
                              height="100%"
                              width="auto"
                              objectFit="contain"
                            />
                          </Flex>
                        </Link>
                      </Box>
                      <Box flex="1" textAlign="center" minW="50%">
                        <Link
                          href="https://accounts.google.com/signin/recovery"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Flex height="40px" alignItems="center" justifyContent="center">
                            <Image
                              src="/img/login/google_logo.svg"
                              alt="Google Logo"
                              height="100%"
                              width="auto"
                              objectFit="contain"
                            />
                          </Flex>
                        </Link>
                      </Box>
                    </Flex>
                  </ModalBody>
                </ModalContent>
              </Modal>
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </main>
  );
}
